import { deduplicateOptions } from '@/shared-utilities';

interface Option {
  label: string;
  value: string;
}

/**
 * Hook to reorder options so that the 'other' option is always last
 * @param options Array of options with value property
 * @returns Reordered array with 'other' option at the end
 */
export const useReorderOtherOptionToLast = (options: Option[]) => {
  if (!options?.length) return options;

  const uniqueOptions = deduplicateOptions(options);

  const otherOptionIndex = uniqueOptions.findIndex((option) =>
    option.value.includes('other')
  );

  if (
    otherOptionIndex === -1 ||
    otherOptionIndex === uniqueOptions.length - 1
  ) {
    return uniqueOptions;
  }

  return [
    ...uniqueOptions.slice(0, otherOptionIndex),
    ...uniqueOptions.slice(otherOptionIndex + 1),
    uniqueOptions[otherOptionIndex],
  ];
};

/**
 * Hook for ProjectResponseSelect component logic
 * Handles value selection and click handling
 *
 * `tentativeValue` is a visual-only hint (e.g. from a live voice transcript).
 * It renders as selected but is ignored by `handleClick` so it never mutates
 * committed form state. Any real `value` takes precedence — once the user
 * clicks, the tentative hint disappears for that question.
 */
export function useProjectResponseSelect(
  value: string,
  onClick: (value: string) => void,
  maxSelections?: number,
  tentativeValue?: string
) {
  const highlightedValue = value || tentativeValue || '';
  const isValueSelected = (selectedValue: string) =>
    highlightedValue.split(',').includes(selectedValue);
  const isValueCommitted = (selectedValue: string) =>
    value.split(',').includes(selectedValue);

  const handleClick = (clickedValue: string) => {
    if (isValueCommitted(clickedValue)) {
      onClick(
        value
          .split(',')
          .filter((v) => v !== clickedValue)
          .join(',')
      );
    } else {
      const currentSelections = value ? value.split(',').filter(Boolean) : [];

      // If we've reached the maximum selection limit, replace the last selection
      if (maxSelections && currentSelections.length >= maxSelections) {
        const newSelections = [...currentSelections.slice(0, -1), clickedValue];
        onClick(newSelections.join(','));
        return;
      }

      if (value) {
        onClick([...value.split(','), clickedValue].join(','));
      } else {
        onClick(clickedValue);
      }
    }
  };

  return {
    isValueSelected,
    handleClick,
  };
}
